/**
 * A section labelled for a human shows a section NUMBER, never a machine id.
 *
 * The browser/add-in twin of the Word renderer's `_is_module_id_slug`
 * (08a884ee): ~20 surfaces used to compose "number + title" inline and would
 * print `§3.2.P.1/size-shape-volume  Size, Shape and Volume Comparison` — the
 * `3.2.P.1/size-shape-volume` is a CMC-ANDA drug-product MODULE ID (one of 30),
 * an internal key, not a section number a reader should ever see.
 *
 * The rule is deliberately narrow: a "module-id slug" is exactly a section id
 * that carries a `/`. Every real CTD / letter-prefixed number (`3.2.P.1`,
 * `A.2.1`, `AD.1`, `3A`, `1`) is kept. The agreement with the Python predicate
 * that classifies the same ids at generation time is pinned by
 * `__fixtures__/sectionIdConformance.json` (see `sectionLabel.test.ts`).
 */

/** A section id that is really an internal module key — it carries a `/` (e.g.
 *  `3.2.P.1/size-shape-volume`). Such ids are never shown to a human. */
export function isModuleIdSlug(n: string): boolean {
  return n.includes("/");
}

/** The number to display for a section, or `""` when there is none to show:
 *  a nullish/empty id, or a module-id slug (which is a key, not a number). */
export function displaySectionNumber(n: string | null | undefined): string {
  if (!n) return "";
  const trimmed = n.trim();
  if (!trimmed || isModuleIdSlug(trimmed)) return "";
  return trimmed;
}

/** Formatting knobs for `sectionLabel`. Defaults compose `§<number> <title>`. */
export interface SectionLabelOptions {
  /** Prefix before the number. Default `"§"`. */
  marker?: string;
  /** Separator between number and title. Default `" "`. */
  separator?: string;
}

/**
 * Compose a human section label from a `number` and `title`.
 *
 *  - a module-id slug (or an absent number) → the title alone (or `""`);
 *  - a real number with a title → `${marker}${number}${separator}${title}`;
 *  - a real number, no title → `${marker}${number}`.
 *
 * Never leaks the raw id: a slug is dropped, not printed.
 */
export function sectionLabel(
  number: string | null | undefined,
  title: string | null | undefined,
  opts: SectionLabelOptions = {},
): string {
  const marker = opts.marker ?? "§";
  const separator = opts.separator ?? " ";
  const num = displaySectionNumber(number);
  const ttl = (title ?? "").trim();
  // No showable number (slug or missing) → the title carries the identity.
  if (!num) return ttl;
  if (!ttl) return `${marker}${num}`;
  return `${marker}${num}${separator}${ttl}`;
}

/** The subset of a run summary the label needs. Permissive so both `RunSummary`
 *  and the run-history rows satisfy it. */
export interface RunSectionLabelLike {
  number?: string | null;
  title?: string | null;
  label?: string | null;
}

/** Label a run by the section it targets — used by the Runs history / capsule
 *  ("Regenerated §3.2.P.1 Description", "Ask · …"). Falls back to the run's own
 *  short `label`, then its raw number, so a run with no resolved title still
 *  reads as something. */
export function runSectionLabel(run: RunSectionLabelLike): string {
  return sectionLabel(run.number, run.title) || run.label || run.number || "";
}
